import path from 'path';
import fs from 'fs-extra';
import chalk from 'chalk';
import { exec } from 'child_process';
import { promisify } from 'util';
import { CliOptions, TemplateVariables } from '../utils/types.js';
import { checkDirectoryExists } from '../utils/validation.js';
import ora from 'ora';
import ejs from 'ejs';

const execAsync = promisify(exec);

/**
 * 创建项目
 * @param options CLI选项
 */
export async function createProject(options: CliOptions): Promise<void> {
  const { projectName, projectType, targetDirectory, force, skipInstall } = options;

  // 检查目标目录
  const projectPath = await checkDirectoryExists(targetDirectory, projectName, force);
  if (!projectPath) {
    process.exit(1);
  }

  // 创建项目文件夹
  try {
    fs.mkdirSync(projectPath, { recursive: true });
  } catch (error) {
    console.error(chalk.red(`创建项目目录失败：${error}`));
    process.exit(1);
  }

  // 准备模板变量
  const templateVars: TemplateVariables = {
    projectName,
    projectType,
    description: options.description,
    author: options.author,
    includeExamples: options.includeExamples,
    year: new Date().getFullYear(),
    serviceName: convertToServiceName(projectName)
  };

  // 复制并处理模板文件
  const spinner = ora('生成项目文件中...').start();

  try {
    // 复制模板文件
    await copyTemplateFiles(projectPath, projectType, templateVars);

    spinner.succeed('项目文件生成成功');

    // 安装依赖
    if (!skipInstall) {
      spinner.text = '安装依赖中...';
      spinner.start();

      try {
        await installDependencies(projectPath);
        spinner.succeed('依赖安装成功');
      } catch (error) {
        spinner.fail('依赖安装失败');
        console.error(chalk.red(`安装依赖失败：${error}`));
        console.log(chalk.yellow('请手动安装依赖：npm install 或 yarn install'));
      }
    }

    // 显示成功信息
    displaySuccessMessage(projectName, projectPath);

  } catch (error) {
    spinner.fail('项目创建失败');
    console.error(chalk.red(`创建项目失败：${error}`));
    process.exit(1);
  }
}

/**
 * 复制并处理模板文件
 * @param targetPath 目标路径
 * @param projectType 项目类型
 * @param variables 模板变量
 */
async function copyTemplateFiles(
  targetPath: string,
  projectType: string,
  variables: TemplateVariables
): Promise<void> {
  // 创建基本目录结构
  const directories = [
    'src',
    'src/config',
    'src/services',
  ];

  for (const dir of directories) {
    fs.mkdirSync(path.join(targetPath, dir), { recursive: true });
  }

  // 根据项目类型选择适当的模板
  const templateDir = path.join(__dirname, '../templates', projectType);

  // 如果模板目录不存在，使用默认模板
  const sourceTemplateDir = fs.existsSync(templateDir) ? templateDir : path.join(__dirname, '../templates/default');

  // 复制并处理所有模板文件
  await copyDirectoryContents(sourceTemplateDir, targetPath, variables);
}

/**
 * 递归复制目录内容
 * @param source 源目录
 * @param target 目标目录
 * @param variables 模板变量
 */
async function copyDirectoryContents(
  source: string,
  target: string,
  variables: TemplateVariables
): Promise<void> {
  const files = fs.readdirSync(source);

  for (const file of files) {
    const sourcePath = path.join(source, file);
    const stats = fs.statSync(sourcePath);

    if (stats.isDirectory()) {
      // 处理目录
      const targetDir = path.join(target, file);
      fs.mkdirSync(targetDir, { recursive: true });
      await copyDirectoryContents(sourcePath, targetDir, variables);
    } else {
      // 处理文件
      const targetFilePath = path.join(target, file);
      const content = fs.readFileSync(sourcePath, 'utf-8');

      // 处理EJS模板变量
      try {
        const processedContent = ejs.render(content, variables);
        fs.writeFileSync(targetFilePath, processedContent);
      } catch (error) {
        // 如果无法处理模板，直接复制文件
        fs.copyFileSync(sourcePath, targetFilePath);
      }
    }
  }
}

/**
 * 安装依赖
 * @param projectPath 项目路径
 */
async function installDependencies(projectPath: string): Promise<void> {
  // 检测包管理器
  const packageManagers = ['pnpm', 'yarn', 'npm'];
  let packageManager = 'npm';

  for (const pm of packageManagers) {
    try {
      await execAsync(`${pm} --version`);
      packageManager = pm;
      break;
    } catch (error) {
      // 继续检测下一个包管理器
    }
  }

  // 执行安装
  await execAsync(`cd ${projectPath} && ${packageManager} install`);
}

/**
 * 显示项目创建成功信息
 * @param projectName 项目名称
 * @param projectPath 项目路径
 */
function displaySuccessMessage(projectName: string, projectPath: string): void {
  console.log();
  console.log(chalk.green('✨ 项目创建成功！'));
  console.log();
  console.log(`项目位置: ${chalk.cyan(projectPath)}`);
  console.log();
  console.log(chalk.yellow('接下来你可以:'));
  console.log(`  ${chalk.cyan('cd')} ${projectName}`);
  console.log(`  ${chalk.cyan('npm run dev')} - 开发模式运行`);
  console.log(`  ${chalk.cyan('npm run build')} - 构建项目`);
  console.log(`  ${chalk.cyan('npm run start:http')} - HTTP模式启动服务`);
  console.log();
  console.log(chalk.blue('愉快地开发吧! 🚀'));
  console.log();
}

/**
 * 将项目名称转换为服务名称
 * @param name 项目名称
 * @returns 服务名称
 */
function convertToServiceName(name: string): string {
  // 将连字符转换为驼峰式命名
  return name
    .split('-')
    .map((part, index) => {
      if (index === 0) {
        return part.charAt(0).toLowerCase() + part.slice(1);
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('') + 'Service';
}
